// Model loader for B4's token-level semantic compressor, kept in its own module
// so the worker around it (compressor-worker.ts) and the algorithm beside it
// (llmlingua2.ts) never have to change when the checkpoint does.
//
// Loads an already-ONNX-exported LLMLingua-2 token classifier through
// `@huggingface/transformers`, exactly the way src/nlp/embeddings.ts loads A3's
// embedder: the library is `await import()`ed inside the loader rather than
// statically imported, because it drags in a ~23MB ONNX Runtime WASM backend
// that must not land in the initial bundle of a user who has B4 turned off. The
// weights themselves are fetched from the Hugging Face Hub on first use and
// cached by transformers.js's own cache — nothing is bundled.
//
// This loader never throws. Every failure (offline, unsupported environment,
// 404 on the Hub) comes back as `{ available: false, reason }`, which the worker
// turns into a `skipped_model_unavailable` status and B4 turns into "left every
// span untouched". That fail-open contract is the whole feature's safety net.

import type { PreTrainedModel, PreTrainedTokenizer } from '@huggingface/transformers';

/**
 * TinyBERT-sized binary token classifier (~57MB fp32, q8 here) trained on
 * MeetingBank with the LLMLingua-2 recipe: 2 output classes per token, class 1
 * = keep, class 0 = discard. WordPiece tokenizer, so `##` marks continuation
 * pieces — llmlingua2.ts's word merging depends on that.
 *
 * Provenance, accepted deliberately: this is a third-party retrain/export
 * (MIT-licensed source repo `atjsh/llmlingua-2-js`), not a Microsoft-published
 * artifact, and the model card carries no separate license file. Trusted on the
 * same footing as the community ONNX export A3 already fetches. Swapping in a
 * first-party export later means changing this one string plus the version
 * below.
 */
const MODEL_ID = 'atjsh/llmlingua-2-js-tinybert-meetingbank';

/** Quantization requested from transformers.js; part of the version string below. */
const MODEL_DTYPE = 'q8';

/**
 * Bumped whenever the model or its decoding changes. Part of the memoization
 * cache key so stale compressions from a previous model never resurface.
 */
export const COMPRESSOR_MODEL_VERSION = 'llmlingua2-tinybert-meetingbank-q8-v1';

export type ModelLoadResult =
  | { available: true; tokenizer: PreTrainedTokenizer; model: PreTrainedModel }
  | { available: false; reason: string };

/**
 * Memoized for the lifetime of the worker, including the failure case: once
 * compressor.ts has been told the model is unavailable it latches
 * `modelKnownUnavailable` and stops sending spans anyway, so there is nothing
 * for a retry to accomplish here.
 */
let loadPromise: Promise<ModelLoadResult> | null = null;

export async function loadCompressorModel(): Promise<ModelLoadResult> {
  loadPromise ??= load();
  return loadPromise;
}

async function load(): Promise<ModelLoadResult> {
  try {
    const { AutoModelForTokenClassification, AutoTokenizer } = await import('@huggingface/transformers');
    // Both fetches hit the same repo; running them together halves the cold-start
    // wall clock, and neither is useful without the other.
    const [tokenizer, model] = await Promise.all([
      AutoTokenizer.from_pretrained(MODEL_ID),
      AutoModelForTokenClassification.from_pretrained(MODEL_ID, { dtype: MODEL_DTYPE }),
    ]);
    return { available: true, tokenizer, model };
  } catch (err) {
    return { available: false, reason: describe(err) };
  }
}

function describe(err: unknown): string {
  if (err instanceof Error && err.message) return `compressor model failed to load: ${err.message}`;
  return 'compressor model failed to load';
}
